/**
 * Admin — Beta Waitlist  ·  /admin/beta-waitlist
 *
 * Wave 5-Late propagation (from BetaCodes v2 anchor).
 *   • AdminLayout wrap with title / subtitle / actions.
 *   • Section primitive w/ hairline `--ds-color-admin-border` dividers.
 *   • Table `border-collapse: collapse` + explicit inline `border-top`
 *     so row hairlines actually paint.
 *   • Row density `py-1.5`.
 *   • Status pills via DS `Badge` with muted admin tones
 *     (`successMuted` / `neutralMuted` / `info` / `accent`).
 *   • Inputs on `--ds-radius-input` (3px) + `--ds-color-input-border`,
 *     `px-2.5 py-1.5` padding.
 *   • Detail drawer rewrapped in the same admin card chrome.
 *
 * All testids, API calls, filter/pagination behavior, and CSV export
 * are preserved BYTE-EXACT.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { Download, Loader2, RefreshCcw, Search, Users, X } from "lucide-react";
import AdminLayout from "@/admin/AdminLayout";
import AdminPageHelp from "@/design-system/components/AdminPageHelp";
import { api, formatApiError } from "@/api/client";
import { Heading } from "@/design-system/components/Heading";
import { Badge } from "@/design-system/components/Badge";
import { AccentButton, GhostButton, Section, SelectInput, TextInput } from "@/admin/_primitives";

const INTEREST_LABELS = {
  credit: "Credit",
  funding: "Funding",
  mortgage: "Mortgage",
  grants: "Grants",
  all: "All",
};
const STATUS_LABELS = {
  new: "New",
  invited: "Invited",
  activated: "Activated",
  declined: "Declined",
};
const STATUS_TONE = {
  new: "info",
  invited: "accent",
  activated: "successMuted",
  declined: "neutralMuted",
};
const PAGE_SIZES = [25, 50, 100];

/* ------------------------------------------------------------------ */
/* Local admin primitives — mirror the BetaCodes v2 anchor.             */
/* ------------------------------------------------------------------ */


function Field({ label, children }) {
  return (
    <label className="block">
      <span
        className="block text-[10.5px] uppercase tracking-[0.16em] font-semibold mb-1"
        style={{ color: "var(--ds-color-ink-subtle)" }}
      >
        {label}
      </span>
      {children}
    </label>
  );
}





/* ------------------------------------------------------------------ */
/* Root                                                                 */
/* ------------------------------------------------------------------ */

export default function AdminBetaWaitlist() {
  const [filters, setFilters] = useState({ interest: "", status: "", q: "" });
  const [debouncedQ, setDebouncedQ] = useState("");
  const [limit, setLimit] = useState(25);
  const [offset, setOffset] = useState(0);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState(null);

  const fetchList = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = { limit, offset };
      if (filters.interest) params.interest = filters.interest;
      if (filters.status) params.status_filter = filters.status;
      if (debouncedQ) params.q = debouncedQ;
      const { data } = await api.get("/admin/beta-waitlist", { params });
      setRows(data?.items || []);
      setTotal(data?.total || 0);
    } catch (err) {
      setError(formatApiError(err) || "Failed to load waitlist.");
    } finally {
      setLoading(false);
    }
  }, [filters.interest, filters.status, debouncedQ, limit, offset]);

  useEffect(() => {
    const id = setTimeout(() => {
      setDebouncedQ(filters.q.trim());
      setOffset(0);
    }, 300);
    return () => clearTimeout(id);
  }, [filters.q]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  const totalPages = Math.max(1, Math.ceil(total / limit));
  const currentPage = Math.floor(offset / limit) + 1;

  const onExport = async () => {
    const url = "/admin/beta-waitlist/export.csv";
    try {
      const res = await api.get(url, { responseType: "blob" });
      const blob = new Blob([res.data], { type: "text/csv" });
      const href = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = href;
      a.download = `beta_waitlist_${new Date().toISOString().slice(0, 19).replace(/[:T]/g, "_")}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(href);
    } catch (err) {
      setError(formatApiError(err) || "CSV export failed.");
    }
  };

  const rowsRendered = useMemo(() => rows, [rows]);

  const headerActions = (
    <GhostButton onClick={onExport} data-testid="admin-beta-export-csv">
      <Download className="w-3.5 h-3.5" /> Export CSV
    </GhostButton>
  );

  const filtersBadge = (
    <GhostButton
      onClick={fetchList}
      data-testid="admin-beta-refresh"
      title="Refresh"
      className="!py-1 !text-[11px]"
    >
      <RefreshCcw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} /> Refresh
    </GhostButton>
  );

  return (
    <AdminLayout
      title="Beta Waitlist"
      subtitle="Requests received from the public beta landing page."
      actions={headerActions}
    >
      <div data-testid="admin-beta-waitlist-page">
        <div className="mb-4">
          <AdminPageHelp
            title="Early Access Waitlist"
            purpose="View and manage the public waitlist entries submitted from the /beta landing page."
            configures="Change waitlist status (Pending → Approved → Invited), export CSV, issue early access codes."
            impact="Status changes trigger onboarding email drips. Approving a waitlist entry does NOT automatically send an invite — issue an access code separately."
            safety="Every status change is logged. Waitlist entries can be re-set to Pending. Deletion is not supported from this UI."
          />
        </div>

        <Section
          title="Filter waitlist"
          icon={Search}
          badge={filtersBadge}
          testid="admin-beta-filters"
        >
          <div className="flex flex-wrap items-end gap-3">
            <div className="flex-1 min-w-[220px]">
              <Field label="Search">
                <div className="flex items-stretch gap-2">
                  <div className="flex-1 relative">
                    <TextInput
                      value={filters.q}
                      onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value }))}
                      placeholder="Search name or email…"
                      data-testid="admin-beta-search"
                    />
                    {filters.q ? (
                      <button
                        type="button"
                        onClick={() => setFilters((f) => ({ ...f, q: "" }))}
                        data-testid="admin-beta-search-clear"
                        className="absolute right-2 top-1/2 -translate-y-1/2 p-0.5 rounded text-[var(--ds-color-ink-muted)] hover:text-[var(--ds-color-ink)]"
                        aria-label="Clear search"
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                    ) : null}
                  </div>
                </div>
              </Field>
            </div>
            <Field label="Interest">
              <SelectInput
                data-testid="admin-beta-filter-interest"
                value={filters.interest}
                onChange={(e) => {
                  setFilters((f) => ({ ...f, interest: e.target.value }));
                  setOffset(0);
                }}
              >
                <option value="">All interests</option>
                {Object.entries(INTEREST_LABELS).map(([v, l]) => (
                  <option key={v} value={v}>{l}</option>
                ))}
              </SelectInput>
            </Field>
            <Field label="Status">
              <SelectInput
                data-testid="admin-beta-filter-status"
                value={filters.status}
                onChange={(e) => {
                  setFilters((f) => ({ ...f, status: e.target.value }));
                  setOffset(0);
                }}
              >
                <option value="">All statuses</option>
                {Object.entries(STATUS_LABELS).map(([v, l]) => (
                  <option key={v} value={v}>{l}</option>
                ))}
              </SelectInput>
            </Field>
          </div>
        </Section>

        {error ? (
          <div
            data-testid="admin-beta-error"
            className="mb-4 rounded-[var(--ds-radius-field)] border px-3 py-2 text-[12px]"
            style={{
              borderColor: "var(--ds-color-danger)",
              color: "var(--ds-color-danger)",
              background: "var(--ds-color-danger-soft)",
            }}
          >
            {error}
          </div>
        ) : null}

        <Section
          title="Requests"
          icon={Users}
          badge={
            <span
              className="text-[11px] uppercase tracking-[0.14em]"
              style={{ color: "var(--ds-color-ink-muted)" }}
            >
              {total} total
            </span>
          }
          testid="admin-beta-table-section"
          bodyClassName="p-0"
        >
          <div className="overflow-x-auto">
            <table
              className="w-full text-[13px]"
              style={{ borderCollapse: "collapse" }}
              data-testid="admin-beta-table"
            >
              <thead>
                <tr
                  className="text-left text-[10px] uppercase tracking-[0.16em] font-semibold"
                  style={{
                    color: "var(--ds-color-ink-subtle)",
                    background: "var(--ds-color-admin-surface-muted)",
                    borderBottom: "1px solid var(--ds-color-admin-border)",
                  }}
                >
                  <Th>Date</Th>
                  <Th>Name</Th>
                  <Th>Email</Th>
                  <Th>Phone</Th>
                  <Th>Interest</Th>
                  <Th>Current client</Th>
                  <Th>Status</Th>
                  <Th />
                </tr>
              </thead>
              <tbody>
                {loading && rowsRendered.length === 0 ? (
                  <tr>
                    <td
                      colSpan={8}
                      className="px-4 py-10 text-center"
                      style={{ color: "var(--ds-color-ink-muted)" }}
                    >
                      <Loader2 className="inline-block w-3.5 h-3.5 animate-spin mr-1.5" />
                      Loading…
                    </td>
                  </tr>
                ) : rowsRendered.length === 0 ? (
                  <tr>
                    <td
                      colSpan={8}
                      className="px-4 py-10 text-center"
                      style={{ color: "var(--ds-color-ink-muted)" }}
                      data-testid="admin-beta-empty"
                    >
                      No beta requests yet.
                    </td>
                  </tr>
                ) : (
                  rowsRendered.map((row) => (
                    <tr
                      key={row.id}
                      data-testid={`admin-beta-row-${row.id}`}
                      style={{ borderTop: "1px solid var(--ds-color-admin-border)" }}
                    >
                      <Td>
                        {row.created_at ? new Date(row.created_at).toLocaleString() : ""}
                      </Td>
                      <Td strong>{row.first_name} {row.last_name}</Td>
                      <Td>
                        <a
                          href={`mailto:${row.email}`}
                          className="hover:underline"
                          style={{ color: "var(--ds-color-accent-strong)" }}
                        >
                          {row.email}
                        </a>
                      </Td>
                      <Td>{row.phone}</Td>
                      <Td>{INTEREST_LABELS[row.interest_area] || row.interest_area}</Td>
                      <Td>{row.is_current_client ? "Yes" : "No"}</Td>
                      <Td>
                        <StatusPill status={row.status} />
                      </Td>
                      <Td>
                        <button
                          type="button"
                          onClick={() => setSelected(row)}
                          data-testid={`admin-beta-row-view-${row.id}`}
                          className="text-[11px] rounded-[var(--ds-radius-field)] border px-2.5 py-1 uppercase tracking-[0.08em]"
                          style={{
                            borderColor: "var(--ds-color-admin-border)",
                            color: "var(--ds-color-ink-muted)",
                            background: "var(--ds-color-admin-surface-muted)",
                          }}
                        >
                          View
                        </button>
                      </Td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          <div
            className="flex flex-wrap items-center justify-between gap-3 px-4 py-2.5 text-[11px]"
            style={{
              borderTop: "1px solid var(--ds-color-admin-border)",
              color: "var(--ds-color-ink-muted)",
              background: "var(--ds-color-admin-surface-muted)",
            }}
            data-testid="admin-beta-pagination"
          >
            <div className="flex items-center gap-2">
              <span className="uppercase tracking-[0.14em]">Page size</span>
              <SelectInput
                value={limit}
                onChange={(e) => {
                  setLimit(Number(e.target.value));
                  setOffset(0);
                }}
                data-testid="admin-beta-page-size"
                className="!px-2 !py-0.5 !text-[12px]"
              >
                {PAGE_SIZES.map((n) => (
                  <option key={n} value={n}>{n}</option>
                ))}
              </SelectInput>
            </div>
            <div className="flex items-center gap-3">
              <span data-testid="admin-beta-page-indicator" className="uppercase tracking-[0.14em]">
                Page {currentPage} of {totalPages} · {total} total
              </span>
              <GhostButton
                onClick={() => setOffset(Math.max(0, offset - limit))}
                disabled={offset === 0}
                data-testid="admin-beta-prev"
                className="!py-0.5 !text-[11px]"
              >
                Prev
              </GhostButton>
              <GhostButton
                onClick={() => setOffset(offset + limit)}
                disabled={offset + limit >= total}
                data-testid="admin-beta-next"
                className="!py-0.5 !text-[11px]"
              >
                Next
              </GhostButton>
            </div>
          </div>
        </Section>
      </div>

      {selected ? (
        <DetailDrawer
          entry={selected}
          onClose={() => setSelected(null)}
          onSaved={(updated) => {
            setRows((prev) => prev.map((r) => (r.id === updated.id ? updated : r)));
            setSelected(updated);
          }}
        />
      ) : null}
    </AdminLayout>
  );
}

/* ------------------------------------------------------------------ */
/* Small helpers                                                        */
/* ------------------------------------------------------------------ */

function Th({ children }) {
  return <th className="px-4 py-2">{children}</th>;
}

function Td({ children, strong = false }) {
  return (
    <td
      className="px-4 py-1.5 align-top"
      style={{ color: strong ? "var(--ds-color-ink)" : "var(--ds-color-ink-muted)" }}
    >
      {children}
    </td>
  );
}

function StatusPill({ status }) {
  const tone = STATUS_TONE[status] || "neutralMuted";
  return (
    <Badge
      tone={tone}
      size="sm"
      data-testid={`admin-beta-status-${status}`}
      className="uppercase tracking-[0.14em]"
    >
      {STATUS_LABELS[status] || status}
    </Badge>
  );
}

/* ------------------------------------------------------------------ */
/* DetailDrawer                                                         */
/* ------------------------------------------------------------------ */

function DetailDrawer({ entry, onClose, onSaved }) {
  const [status, setStatus] = useState(entry.status || "new");
  const [betaCode, setBetaCode] = useState(entry.beta_code_issued || "");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);

  const dirty = status !== entry.status || (betaCode || "") !== (entry.beta_code_issued || "");

  const onSave = async () => {
    if (!dirty || saving) return;
    setSaving(true);
    setErr(null);
    try {
      const body = {};
      if (status !== entry.status) body.status = status;
      if ((betaCode || "") !== (entry.beta_code_issued || "")) {
        body.beta_code_issued = betaCode || "";
      }
      const { data } = await api.patch(`/admin/beta-waitlist/${entry.id}`, body);
      onSaved(data);
    } catch (e) {
      setErr(formatApiError(e) || "Save failed.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      data-testid="admin-beta-drawer"
      className="fixed inset-0 z-40 flex justify-end"
      style={{ background: "rgba(15,18,24,0.55)" }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="h-full w-full max-w-md overflow-y-auto"
        style={{
          background: "var(--ds-color-surface)",
          borderLeft: "1px solid var(--ds-color-admin-border)",
        }}
      >
        <div
          className="flex items-center justify-between px-5 py-4"
          style={{ borderBottom: "1px solid var(--ds-color-admin-border)" }}
        >
          <div>
            <div
              className="text-[10px] uppercase tracking-[0.18em] font-semibold"
              style={{ color: "var(--ds-color-ink-subtle)" }}
            >
              Beta request
            </div>
            <Heading level={3} size="sm" weight="semibold" className="mt-0.5" data-testid="admin-beta-drawer-name">
              {entry.first_name} {entry.last_name}
            </Heading>
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="admin-beta-drawer-close"
            className="p-1 rounded text-[var(--ds-color-ink-muted)] hover:text-[var(--ds-color-ink)]"
            aria-label="Close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="p-5 space-y-4 text-[13px]" style={{ color: "var(--ds-color-ink)" }}>
          <KV label="Email" value={entry.email} testId="admin-beta-drawer-email" />
          <KV label="Phone" value={entry.phone} testId="admin-beta-drawer-phone" />
          <KV
            label="Interest area"
            value={INTEREST_LABELS[entry.interest_area] || entry.interest_area}
            testId="admin-beta-drawer-interest"
          />
          <KV
            label="Current client"
            value={entry.is_current_client ? "Yes" : "No"}
            testId="admin-beta-drawer-client"
          />
          <KV
            label="Submitted"
            value={entry.created_at ? new Date(entry.created_at).toLocaleString() : ""}
            testId="admin-beta-drawer-submitted"
          />

          <div>
            <div
              className="text-[10px] uppercase tracking-[0.18em] font-semibold mb-1.5"
              style={{ color: "var(--ds-color-ink-subtle)" }}
            >
              Message
            </div>
            <div
              className="rounded-[var(--ds-radius-input)] border p-3 text-[13px] whitespace-pre-wrap"
              style={{
                borderColor: "var(--ds-color-admin-border)",
                background: "var(--ds-color-admin-surface-muted)",
              }}
              data-testid="admin-beta-drawer-message"
            >
              {entry.message || <span style={{ color: "var(--ds-color-ink-muted)" }}>—</span>}
            </div>
          </div>

          <div style={{ borderTop: "1px solid var(--ds-color-admin-border)" }} className="pt-4 space-y-4">
            <Field label="Status">
              <SelectInput
                value={status}
                onChange={(e) => setStatus(e.target.value)}
                data-testid="admin-beta-drawer-status"
                className="w-full"
              >
                {Object.entries(STATUS_LABELS).map(([v, l]) => (
                  <option key={v} value={v}>{l}</option>
                ))}
              </SelectInput>
            </Field>
            <Field label="Beta code issued">
              <TextInput
                value={betaCode}
                onChange={(e) => setBetaCode(e.target.value)}
                placeholder="Leave blank if none"
                data-testid="admin-beta-drawer-beta-code"
              />
            </Field>

            {err ? (
              <div
                className="rounded-[var(--ds-radius-field)] border px-3 py-2 text-[12px]"
                style={{
                  borderColor: "var(--ds-color-danger)",
                  color: "var(--ds-color-danger)",
                  background: "var(--ds-color-danger-soft)",
                }}
                data-testid="admin-beta-drawer-error"
              >
                {err}
              </div>
            ) : null}

            <div className="flex justify-end gap-2 pt-2">
              <GhostButton onClick={onClose} data-testid="admin-beta-drawer-cancel">
                Close
              </GhostButton>
              <AccentButton
                onClick={onSave}
                disabled={!dirty || saving}
                data-testid="admin-beta-drawer-save"
              >
                {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}
                Save changes
              </AccentButton>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function KV({ label, value, testId }) {
  return (
    <div className="flex justify-between gap-4">
      <span
        className="text-[10.5px] uppercase tracking-[0.16em] font-semibold"
        style={{ color: "var(--ds-color-ink-subtle)" }}
      >
        {label}
      </span>
      <span data-testid={testId} className="text-right break-all">
        {value || <span style={{ color: "var(--ds-color-ink-muted)" }}>—</span>}
      </span>
    </div>
  );
}
